import {
  Dimensions,
  ScrollView,
  StyleSheet,
  View,
  Text,
  Platform,
  Keyboard,
} from "react-native";
import React, { useContext, useEffect, useRef, useState } from "react";
import ProgressBar from "../../components/ProgressBar";
import QuestionInfo from "./QuestionInfo";
import Colors from "../../constants/Colors";
import { Feather } from "@expo/vector-icons";
import QuestionSelect from "./QuestionSelect";
import QuestionInput from "./QuestionInput";
import QuestionThanks from "./QuestionThanks";
import GlobalContext from "../../context/Global";
import { useGetFormQuestions } from "../../hooks/useGetFormQuestions";
import { useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import useOpenHouseAttendeeSubmission from "../../hooks/useOpenHouseAttendeeSubmission";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import Pressable from "../../components/Pressable";

const { width } = Dimensions.get("window");

interface SignInProps {
  openHouse: any;
  step: number;
  setStep: (step: number) => void;
  change: boolean;
}

const LiteSignIn: React.FC<SignInProps> = ({
  openHouse,
  step,
  setStep,
  change,
}) => {
  const totalSteps = 1;
  const scrollRef = useRef<ScrollView>(null);
  const { data: questions } = useGetFormQuestions(openHouse?.form_uuid, false);
  const { user } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  const { submitAttendee, isSubmitting } = useOpenHouseAttendeeSubmission(
    openHouse?.uuid,
    user?.id
  );

  const [attendee, setAttendee] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    intent: "",
    collectedForm: {
      collectedQuestionsAndAnswers: {},
    },
  });
  const [disabled, setDisabled] = useState(false);
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    setValue,
  } = useForm();

  useEffect(() => {
    if (step === 0) {
      setDisabled(false);
      checkAttendeeValues();
    } else if (step >= totalSteps + 1) {
      setTimeout(() => {
        checkAttendeeValues();
      }, 250);
    }
  }, [step, change]);

  const clearData = () => {
    setValue("first_name", "");
    setValue("last_name", "");
    setValue("email", "");
    setValue("phone", "");
    setValue("address", "");
    setAttendee({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      intent: "",
      collectedForm: {
        collectedQuestionsAndAnswers: {},
      },
    });
    setStep(0);
    setDisabled(false);
  };

  const checkAttendeeValues = () => {
    const { email, phone, first_name, last_name } = getValues();
    if (email && phone && first_name && last_name) {
      saveAttendee({
        first_name,
        last_name,
        email,
        phone,
        intent: attendee?.intent || "",
        collectedForm: {
          collectedQuestionsAndAnswers:
            attendee?.collectedForm.collectedQuestionsAndAnswers || {},
        },
      });
    } else {
      clearData();
    }
  };

  const _nextStep = async () => {
    goToNextStep();
  };

  const saveAttendee = async (_temp: any | null) => {
    if (isSubmitting) return;
    const attendeeToSave = _temp ?? attendee;
    const answers =
      attendeeToSave.collectedForm?.collectedQuestionsAndAnswers ?? {};
    const payload = {
      ...attendeeToSave,
      ...(Object.keys(answers).length
        ? { collectedForm: { ...attendeeToSave.collectedForm } }
        : { collectedForm: undefined }),
    };

    setDisabled(true);
    try {
      const result = await submitAttendee(payload);
      console.log(`Attendee submission ${result.status}.`);
      queryClient.setQueryData(
        ["openHouseDetails", openHouse?.uuid],
        (oldData: any) => ({ ...oldData, attendees: result.attendees })
      );
      try {
        await queryClient.invalidateQueries(["getTags"]);
      } catch (error) {
        console.error("Attendee saved, but tag refresh failed:", error);
      }
      clearData();
      setStep(0);
    } catch (error) {
      console.error("Unable to persist attendee:", error);
      alert("Your information could not be saved. Please try again.");
    } finally {
      setDisabled(false);
    }
  };

  const goToNextStep = () => {
    if (step !== 2 && step !== 4) {
      setDisabled(true);
    }
    let nextStep = step + 1;
    scrollRef.current?.scrollTo({ x: nextStep * width, y: 0, animated: true });
    setStep(nextStep);
  };

  const handleSelect = (value: string, question: string) => {
    setAttendee({
      ...attendee,
      collectedForm: {
        collectedQuestionsAndAnswers: {
          ...attendee.collectedForm.collectedQuestionsAndAnswers,
          [value.question_uuid]: [
            {
              questionText: question,
              answerUuid: value.uuid,
              answer: value.label,
            },
          ],
        },
      },
    });
    setDisabled(false);
  };

  const submit = async (data: any) => {
    setAttendee({
      ...attendee,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
    });
    goToNextStep();
  };

  const submitFirstStep = () => {
    Keyboard.dismiss();
    void handleSubmit(submit, (validationErrors) => {
      console.warn("Check-in contact validation failed:", validationErrors);
    })();
  };

  const handleUnhandledTouches = () => {
    Keyboard.dismiss();
    return false;
  };

  return (
    <KeyboardAwareScrollView
      extraScrollHeight={200}
      contentContainerStyle={styles.body}
      onStartShouldSetResponder={handleUnhandledTouches}
    >
      <View style={styles.container}>
        <ProgressBar step={step + 1} totalSteps={totalSteps + 1} />
      </View>
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEnabled={false}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        {step === 0 ? (
          <QuestionInfo
            control={control}
            errors={errors}
            question="Please sign in"
            details="Upon the homeowner’s request, we ask that all open house attendees sign in."
          />
        ) : null}
        {questions?.map((question, index) => {
          if (step === 1) {
            if (question.question_type !== 5) {
              return (
                <QuestionSelect
                  key={question.uuid}
                  question={question.question}
                  details={question.details}
                  options={[]}
                  onPress={(value) => {
                    handleSelect(value, question.question);
                  }}
                  uuid={question.uuid}
                />
              );
            }
          }
        })}
        {step === 2 ? (
          <QuestionThanks question="Thank you for signing in!" details="" />
        ) : null}
      </ScrollView>
      <View style={styles.buttonRow}>
        {step !== totalSteps + 1 && (
          <Pressable
            style={disabled ? styles.disabledButton : styles.button}
            onPress={step === 0 ? submitFirstStep : _nextStep}
            disabled={disabled}
          >
            <Text style={styles.buttonText}>
              {step === totalSteps ? "Submit" : "Next"}
            </Text>
            {step !== totalSteps ? (
              <Feather name="arrow-right" size={26} color="white" />
            ) : null}
          </Pressable>
        )}
      </View>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  body: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
    minHeight: "100%",
    backgroundColor: "transparent",
  },
  container: {
    marginTop: 10,
    width: 500,
  },
  buttonRow: {
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    backgroundColor: Colors.primary_500,
    paddingHorizontal: 25,
    paddingVertical: 12,
    borderRadius: 40,
    flexDirection: "row",
    alignItems: "center",
    width: "80%",
    maxWidth: 300,
    justifyContent: "center",
  },
  disabledButton: {
    backgroundColor: Colors.nuetral_grey_400,
    paddingHorizontal: 25,
    paddingVertical: 12,
    borderRadius: 40,
    flexDirection: "row",
    alignItems: "center",
    width: "80%",
    maxWidth: 300,
    justifyContent: "center",
  },
  buttonText: {
    color: "white",
    fontSize: Platform.OS === "web" ? 24 : 20,
    fontFamily: "NunitoSemiBold",
    marginRight: 8,
  },
});

export default LiteSignIn;
